import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../../core/auth/auth.service';
import { AlertService } from '../../../shared/services/alert.service';
import { OtpPurpose } from '../../../core/models/otp-purpose.enum';

@Component({
  selector: 'app-otp-verification',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, ReactiveFormsModule],
  templateUrl: './otp-verification.component.html',
  styleUrls: ['./otp-verification.component.scss']
})
export class OtpVerificationComponent implements OnInit, OnDestroy {
  otpForm: FormGroup;
  phoneNumber: string = '';
  type: string = ''; // 'register' or 'reset'
  timer: number = 30;
  interval: any;

  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private auth = inject(AuthService);
  private alertService = inject(AlertService);
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    this.otpForm = this.fb.group({
      otp1: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      otp2: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      otp3: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      otp4: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      otp5: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      otp6: ['', [Validators.required, Validators.pattern(/^\d$/)]]
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.phoneNumber = params['phone'] || '';
      this.type = params['type'] || '';
    });
    this.startTimer();
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  startTimer(): void {
    this.timer = 30;
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.interval = setInterval(() => {
      if (this.timer > 0) {
        this.timer--;
        this.cdr.detectChanges();
      } else {
        clearInterval(this.interval);
        this.cdr.detectChanges();
      }
    }, 1000);
  }

  resendCode(): void {
    if (this.timer === 0) {
      this.auth.sendOtp(this.phoneNumber).subscribe({
        next: () => {
          this.alertService.success('OTP kod yenidən göndərildi.');
          this.startTimer();
        },
        error: (err) => {
          const errorObj = err.error?.error || err.error;
          const errorCode = errorObj?.code || 'UNKNOWN_ERROR';
          const errorDetails = errorObj?.details;
          this.alertService.errorByCode(errorCode, errorDetails);
        }
      });
    }
  }

  onInput(event: any, nextInputId: string | null): void {
    const input = event.target;
    const value = input.value;

    if (value && nextInputId) {
      const nextInput = document.getElementById(nextInputId) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  onKeyDown(event: KeyboardEvent, prevInputId: string | null): void {
    if (event.key === 'Backspace') {
      const input = event.target as HTMLInputElement;
      if (!input.value && prevInputId) {
        const prevInput = document.getElementById(prevInputId) as HTMLInputElement;
        if (prevInput) {
          prevInput.focus();
        }
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasteData = event.clipboardData?.getData('text').trim();
    if (pasteData && /^\d{6}$/.test(pasteData)) {
      const digits = pasteData.split('');
      this.otpForm.patchValue({
        otp1: digits[0],
        otp2: digits[1],
        otp3: digits[2],
        otp4: digits[3],
        otp5: digits[4],
        otp6: digits[5]
      });
      // Sonuncu inputa focus
      const lastInput = document.getElementById('otp6') as HTMLInputElement;
      if (lastInput) {
        lastInput.focus();
      }
    }
  }

  onSubmit(): void {
    if (this.otpForm.valid) {
      const otpCode = Object.values(this.otpForm.value).join('');
      const purpose = this.type === 'register' ? OtpPurpose.Register : OtpPurpose.ResetPassword;

      this.auth.verifyOtp(this.phoneNumber, otpCode, purpose).subscribe({
        next: (res) => {
          if (this.type === 'register') {
            const pendingData = this.auth.getPendingRegisterData();
            if (pendingData) {
              this.auth.register(pendingData).subscribe({
                next: () => {
                  this.auth.clearPendingRegisterData();
                  this.alertService.success('Qeydiyyat uğurla tamamlandı.');
                  this.router.navigate(['/login']);
                },
                error: (err) => {
                  const errorObj = err.error?.error || err.error;
                  const errorCode = errorObj?.code || 'UNKNOWN_ERROR';
                  const errorDetails = errorObj?.details;
                  this.alertService.errorByCode(errorCode, errorDetails);
                }
              });
            } else {
              this.alertService.error('Qeydiyyat məlumatları tapılmadı.');
              this.router.navigate(['/register']);
            }
          } else {
            // Şifrə sıfırlama (forgot password) axını üçün
            this.router.navigate(['/reset-password'], { queryParams: { phone: this.phoneNumber, code: otpCode } });
          }
        },
        error: (err) => {
          const errorObj = err.error?.error || err.error;
          const errorCode = errorObj?.code || 'UNKNOWN_ERROR';
          const errorDetails = errorObj?.details;
          this.alertService.errorByCode(errorCode, errorDetails);
        }
      });
    } else {
      this.otpForm.markAllAsTouched();
    }
  }
}
